import {
  TopUpOptionsSchema,
  TopUpResultSchema,
  TopUpSchema,
  WalletEntrySchema,
} from "@uzverse/contracts";
import type { TopUp, TopUpOptions, WalletEntry } from "@uzverse/contracts";
import { useSyncExternalStore } from "react";
import { invokeWorldTool } from "../data.js";
import { navStore } from "../camera/nav.js";
import { locoStatus } from "../loco/status.js";
import { LIGHT, vehicleHub } from "./hub.js";
import type { PoseMessage } from "./hub.js";
import type { CarState } from "./physics.js";
import type { VehicleSim } from "./drive/sim.js";
import { restPose } from "./car/instance.js";
import type { CarPose } from "./car/instance.js";
import type { RimStyle } from "./car/paint.js";
import { degToRad, parseModel, parseVehicle, radToDeg, wrapAngle } from "./types.js";
import type { VehicleEntity, VehicleModel } from "./types.js";
import type { DealerInfo } from "./showroom.js";

/**
 * Client state of the vehicle system: the catalog, the user's cars, the wallet, the Galeri panel
 * and which car is being driven. Server truth arrives through the world tools (the vehicles
 * backend); live poses of other drivers arrive as `vehicle.pose` messages and are interpolated by
 * the layer from `poses`. Per-frame values (the driven car's physics state) are plain fields: they
 * are read in `useFrame`, never rendered through React.
 */

export type NearTarget = { kind: "vehicle" | "dealer"; id: string; dist: number } | null;

/** The "Kredi yükle" dialog: options, the ledger, and a paid top-up that waits for its checkout. */
export interface TopUpUiState {
  open: boolean;
  tab: "buy" | "history";
  options: TopUpOptions | null;
  history: WalletEntry[];
  busy: boolean;
  /** i18n key suffix: `failed`, `topUpLimit`, `topUpUnavailable`. */
  error: string | null;
  /** A paid top-up whose checkout page is open; we poll until it completes. */
  pending: { id: string; url: string } | null;
  /** Credits added by the last completed top-up (the dialog and the toast show it). */
  lastAdded: number | null;
}

export interface VehiclesState {
  catalog: VehicleModel[];
  /** The dealership `vehicles.catalog` names (stock, placement). */
  dealership: DealerInfo | null;
  mine: VehicleEntity[];
  credits: number | null;
  /** The Galeri panel. */
  galeri: { open: boolean; dealerId: string | null; tab: "shop" | "mine" };
  topUp: TopUpUiState;
  /** The model, color and rims shown in the panel (the studio car takes them live). */
  preview: { modelId: string | null; color: string | null; rim: RimStyle };
  /** A purchase waiting for its confirmation (model id). */
  confirm: string | null;
  /** A car on a test drive (local only, never on the server) and when it ends (Date.now()). */
  testCar: VehicleEntity | null;
  testEndsAt: number;
  /** A car being delivered to the bay after a purchase (the cover reveal and the camera move). */
  delivering: { id: string; at: number } | null;
  drivingId: string | null;
  near: NearTarget;
  busy: boolean;
  /** Last user-facing problem: an i18n key suffix (`insufficient`, `failed`). */
  error: string | null;
  /** The id of a car that just arrived in the bay (the reveal runs in the layer). */
  lastPurchase: string | null;
  /** Bumped when remote vehicle data changes in place. */
  rev: number;
}

export interface RemotePose {
  x: number;
  z: number;
  yaw: number;
  speed: number;
  steer?: number;
  lights?: number;
  /** performance.now() of arrival. */
  at: number;
}

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {};

const listOf = <T>(v: unknown, key: string, parse: (x: unknown) => T | null): T[] => {
  const raw = Array.isArray(v) ? v : obj(v)[key];
  return Array.isArray(raw) ? raw.map(parse).filter((x): x is T => x !== null) : [];
};

const creditsOf = (v: unknown): number | null => {
  const o = obj(v);
  const direct = o["credits"] ?? obj(o["wallet"])["credits"];
  return typeof direct === "number" && Number.isFinite(direct) ? direct : null;
};

function withoutDriver(v: VehicleEntity): VehicleEntity {
  const { driverUserId: _gone, ...rest } = v;
  return rest;
}

export class VehicleStore {
  private state: VehiclesState = {
    catalog: [],
    dealership: null,
    mine: [],
    credits: null,
    galeri: { open: false, dealerId: null, tab: "shop" },
    topUp: {
      open: false,
      tab: "buy",
      options: null,
      history: [],
      busy: false,
      error: null,
      pending: null,
      lastAdded: null,
    },
    preview: { modelId: null, color: null, rim: "stock" },
    confirm: null,
    testCar: null,
    testEndsAt: 0,
    delivering: null,
    drivingId: null,
    near: null,
    busy: false,
    error: null,
    lastPurchase: null,
    rev: 0,
  };
  private listeners = new Set<() => void>();

  /** The driven car's physics state (written by the drive rig every frame). */
  car: CarState | null = null;
  /** The driven car as drawn (interpolated chassis pose), null until the rig has one. */
  chassis: { x: number; y: number; z: number; yaw: number; pitch: number; roll: number } | null =
    null;
  /** The driven (or boarded) car's moving parts and lamps, written by the rig every frame. */
  readonly visual: CarPose = restPose();
  /** Speed in m/s (signed) and braking flag of the driven car, for HUD and lights. */
  speed = 0;
  braking = false;
  reversing = false;
  throttling = false;
  /** Latest pose of other drivers' vehicles by id (interpolated in the layer). */
  readonly poses = new Map<string, RemotePose>();
  /** Model ids the player may drive right now. */
  private pendingEnter: string | null = null;
  /** Camera while driving: chase, cockpit (with the dashboard) or hood. */
  camMode: "chase" | "cockpit" | "hood" = "chase";
  /** Where the Galeri studio's turntable stands (world), set by the vehicle layer. */
  studioAt: { x: number; z: number; yaw: number } | null = null;
  /** The running simulation (the drive rig sets it), engine speed and gear for the HUD and audio. */
  sim: VehicleSim | null = null;
  rpm = 0;
  gear = 1;
  /** Indicators (cosmetic). */
  indicator: "off" | "left" | "right" | "hazard" = "off";
  /** No pedals while getting in or out. */
  controlsLocked = false;
  /** The engine runs (started after the door closed, stopped when getting out). */
  engineOn = false;
  /** A requested teleport of the driven car (tests, the respawn). */
  teleport: { x: number; z: number; yaw: number } | null = null;
  /** The user id of the viewer (the session's user, set by the UI). */
  selfUserId = "";
  /** performance.now() when each car was revealed after a purchase. */
  readonly reveal = new Map<string, number>();

  constructor() {
    vehicleHub.onPose((m) => this.onRemotePose(m));
  }

  getState = (): VehiclesState => this.state;
  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private set(patch: Partial<VehiclesState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  setSelf(userId: string): void {
    this.selfUserId = userId;
  }

  modelOf(id: string): VehicleModel | undefined {
    return this.state.catalog.find((m) => m.id === id);
  }

  vehicleOf(id: string): VehicleEntity | undefined {
    const t = this.state.testCar;
    if (t && t.id === id) return t;
    return this.state.mine.find((v) => v.id === id);
  }

  // ------------------------------------------------------------------ server reads

  async refresh(): Promise<void> {
    try {
      const [cat, mine, wallet] = await Promise.all([
        invokeWorldTool("vehicles.catalog", {}),
        invokeWorldTool("vehicles.mine", {}),
        invokeWorldTool("wallet.balance", {}),
      ]);
      const catalog = listOf(cat.output, "models", parseModel);
      const owned = listOf(mine.output, "vehicles", parseVehicle);
      // Keep the driven car's live pose; the server copy lags it.
      const d = obj(obj(cat.output)["dealership"]);
      const dealership: DealerInfo | null =
        typeof d["id"] === "string"
          ? {
              id: d["id"],
              name: typeof d["name"] === "string" ? d["name"] : "Galeri",
              placementId: typeof d["placementId"] === "string" ? d["placementId"] : "",
              stockModelIds: Array.isArray(d["stockModelIds"])
                ? d["stockModelIds"].filter((x): x is string => typeof x === "string")
                : [],
            }
          : this.state.dealership;
      this.set({
        dealership,
        catalog: catalog.length > 0 ? catalog : this.state.catalog,
        mine: owned,
        credits: creditsOf(wallet.output) ?? this.state.credits,
      });
    } catch {
      // The vehicle service is optional: no catalog, no galeri.
    }
  }

  // ------------------------------------------------------------------ wallet top-up

  /** How often a pending checkout is polled (tests shorten it). */
  pollMs = 2000;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private setTopUp(patch: Partial<TopUpUiState>): void {
    this.set({ topUp: { ...this.state.topUp, ...patch } });
  }

  openTopUp(): void {
    this.setTopUp({ open: true, tab: "buy", error: null, lastAdded: null });
    void this.loadTopUp();
  }

  closeTopUp(): void {
    this.setTopUp({ open: false });
  }

  setTopUpTab(tab: "buy" | "history"): void {
    this.setTopUp({ tab });
    if (tab === "history") void this.loadHistory();
  }

  /** Options (provider, packages, limits) and the balance. */
  async loadTopUp(): Promise<void> {
    try {
      const [opts, wallet] = await Promise.all([
        invokeWorldTool("wallet.topUpOptions", {}),
        invokeWorldTool("wallet.balance", {}),
      ]);
      const options = TopUpOptionsSchema.safeParse(opts.output);
      const credits = creditsOf(wallet.output);
      this.set({
        ...(credits !== null ? { credits } : {}),
        topUp: { ...this.state.topUp, options: options.success ? options.data : null },
      });
    } catch {
      this.setTopUp({ options: null });
    }
  }

  async loadHistory(): Promise<void> {
    try {
      const out = await invokeWorldTool("wallet.history", { limit: 50 });
      const raw = obj(out.output)["entries"];
      const history = Array.isArray(raw)
        ? raw.flatMap((e) => {
            const p = WalletEntrySchema.safeParse(e);
            return p.success ? [p.data] : [];
          })
        : [];
      this.setTopUp({ history });
    } catch {
      // The ledger is a nicety: an empty list says nothing wrong.
    }
  }

  /**
   * Buys credits by package or a custom amount. A free provider answers `completed` at once; a
   * paid one answers `pending` with a checkout URL, which opens in a new tab while we poll.
   */
  async topUp(input: {
    packageId?: string;
    credits?: number;
  }): Promise<"completed" | "pending" | null> {
    if (this.state.topUp.busy) return null;
    this.setTopUp({ busy: true, error: null, lastAdded: null });
    try {
      const out = await invokeWorldTool("wallet.topUp", input);
      const parsed = TopUpResultSchema.safeParse(out.output);
      if (!parsed.success) throw new Error("bad top-up answer");
      const { topUp, wallet } = parsed.data;
      this.set({ credits: wallet.credits });
      if (topUp.status === "completed") {
        this.setTopUp({ busy: false, lastAdded: topUp.credits, pending: null });
        void this.loadHistory();
        return "completed";
      }
      if (topUp.status === "pending" && topUp.checkoutUrl) {
        this.setTopUp({ busy: false, pending: { id: topUp.id, url: topUp.checkoutUrl } });
        globalThis.open?.(topUp.checkoutUrl, "_blank", "noopener");
        this.pollTopUp(topUp.id);
        return "pending";
      }
      throw new Error("top-up failed");
    } catch (e) {
      const code = (e as { code?: string }).code;
      this.setTopUp({
        busy: false,
        error:
          code === "rate_limited"
            ? "topUpLimit"
            : code === "unavailable"
              ? "topUpUnavailable"
              : "failed",
      });
      return null;
    }
  }

  private pollTopUp(id: string): void {
    this.stopPolling();
    const startedAt = Date.now();
    this.pollTimer = setInterval(() => {
      void (async () => {
        try {
          const out = await invokeWorldTool("wallet.topUpStatus", { topUpId: id });
          const t: TopUp | null = (() => {
            const p = TopUpSchema.safeParse(out.output);
            return p.success ? p.data : null;
          })();
          if (!t || this.state.topUp.pending?.id !== id) return this.stopPolling();
          if (t.status === "completed") {
            this.stopPolling();
            this.setTopUp({ pending: null, lastAdded: t.credits });
            void this.loadTopUp();
            void this.loadHistory();
          } else if (t.status === "failed") {
            this.stopPolling();
            this.setTopUp({ pending: null, error: "failed" });
          }
        } catch {
          // A missed poll is retried on the next tick.
        }
        if (Date.now() - startedAt > 15 * 60_000) this.stopPolling();
      })();
    }, this.pollMs);
  }

  stopPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  /** Gives up waiting for a payment page (the top-up stays pending on the server). */
  cancelPending(): void {
    this.stopPolling();
    this.setTopUp({ pending: null });
  }

  // ------------------------------------------------------------------ panel

  openGaleri(dealerId: string | null, tab: "shop" | "mine" = "shop"): void {
    const first = this.state.catalog[0];
    const modelId = this.state.preview.modelId ?? first?.id ?? null;
    const model = modelId ? this.modelOf(modelId) : undefined;
    this.set({
      galeri: { open: true, dealerId, tab },
      preview: {
        modelId,
        color: this.state.preview.color ?? model?.colors[0] ?? null,
        rim: this.state.preview.rim,
      },
      error: null,
    });
    void this.refresh();
  }

  closeGaleri(): void {
    this.set({ galeri: { ...this.state.galeri, open: false } });
  }

  setTab(tab: "shop" | "mine"): void {
    this.set({ galeri: { ...this.state.galeri, tab } });
  }

  preview(modelId: string, color?: string): void {
    const model = this.modelOf(modelId);
    const keep =
      color ??
      (model?.colors.includes(this.state.preview.color ?? "") ? this.state.preview.color : null);
    this.set({
      preview: { modelId, color: keep ?? model?.colors[0] ?? null, rim: this.state.preview.rim },
      confirm: null,
    });
  }

  setPreviewColor(color: string): void {
    this.set({ preview: { ...this.state.preview, color } });
  }

  setPreviewRim(rim: RimStyle): void {
    this.set({ preview: { ...this.state.preview, rim } });
  }

  /** Rim finishes of the user's cars (cosmetic, kept in this browser). */
  rimOf(vehicleId: string): RimStyle {
    if (this.state.testCar?.id === vehicleId) return this.state.preview.rim;
    return this.rims[vehicleId] ?? "stock";
  }

  private rims: Record<string, RimStyle> = (() => {
    try {
      return JSON.parse(globalThis.localStorage?.getItem("uz.vehicleRims") ?? "{}") as Record<
        string,
        RimStyle
      >;
    } catch {
      return {};
    }
  })();

  setRim(vehicleId: string, rim: RimStyle): void {
    this.rims = { ...this.rims, [vehicleId]: rim };
    try {
      globalThis.localStorage?.setItem("uz.vehicleRims", JSON.stringify(this.rims));
    } catch {
      // Not kept.
    }
    this.set({ rev: this.state.rev + 1 });
  }

  /** Asks for the purchase confirmation (the dialog shows the model and the price). */
  askBuy(modelId: string): void {
    this.set({ confirm: modelId, error: null });
  }

  cancelBuy(): void {
    this.set({ confirm: null });
  }

  // ------------------------------------------------------------------ test drive

  /** How long a test drive lasts (ms). */
  testMs = 120_000;
  private testTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * A test drive: a temporary car of `modelId` appears at the bay, the player gets in and has two
   * minutes; then it is over (the player gets out and the car goes). Local only: nothing is
   * bought, the server never sees the car.
   */
  testDrive(modelId: string, color: string): boolean {
    const model = this.modelOf(modelId);
    if (!model || this.state.drivingId) return false;
    const bay = this.state.dealership?.bay ?? { x: 0, z: -52, yawDeg: 0 };
    const car: VehicleEntity = {
      id: `test-${modelId}`,
      modelId,
      color,
      ownerUserId: this.selfUserId,
      state: "parked",
      pose: { x: bay.x + 9, z: bay.z - 2, yawDeg: bay.yawDeg },
      allowedUserIds: [],
      odometerM: 0,
    } as VehicleEntity;
    this.closeGaleri();
    this.set({ testCar: car, testEndsAt: Date.now() + this.testMs });
    this.startDriving(car);
    if (this.testTimer) clearTimeout(this.testTimer);
    this.testTimer = setTimeout(() => {
      if (this.state.drivingId === car.id) this.requestExit();
    }, this.testMs);
    return true;
  }

  private startDriving(v: VehicleEntity): void {
    const w = navStore.walker;
    this.boardFrom = { x: w.x, z: w.z, facing: locoStatus.facing };
    this.exitRequested = false;
    this.car = null;
    this.speed = 0;
    this.pendingEnter = v.id;
    this.set({ drivingId: v.id, near: null });
    const mode = navStore.getState().mode;
    if (mode !== "first_person" && mode !== "third_person") navStore.setMode("third_person");
  }

  setNear(near: NearTarget): void {
    const a = this.state.near;
    if (a?.kind === near?.kind && a?.id === near?.id) return;
    this.set({ near });
  }

  // ------------------------------------------------------------------ actions

  private async run<T>(fn: () => Promise<T>): Promise<T | null> {
    if (this.state.busy) return null;
    this.set({ busy: true, error: null });
    try {
      return await fn();
    } catch (e) {
      const text = String((e as { message?: string }).message ?? e);
      const code = JSON.stringify((e as { details?: unknown }).details ?? "");
      this.set({
        error: /insufficient|credit|funds/i.test(text + code) ? "insufficient" : "failed",
      });
      return null;
    } finally {
      this.set({ busy: false });
    }
  }

  async buy(modelId: string, color: string): Promise<VehicleEntity | null> {
    const out = await this.run(() => invokeWorldTool("vehicles.buy", { modelId, color }));
    if (!out) return null;
    const vehicle = parseVehicle(obj(out.output)["vehicle"] ?? out.output);
    if (!vehicle) {
      this.set({ error: "failed" });
      return null;
    }
    this.reveal.set(vehicle.id, performance.now());
    if (this.state.preview.rim !== "stock") this.setRim(vehicle.id, this.state.preview.rim);
    const credits = creditsOf(out.output);
    this.set({
      mine: [...this.state.mine.filter((v) => v.id !== vehicle.id), vehicle],
      lastPurchase: vehicle.id,
      confirm: null,
      delivering: { id: vehicle.id, at: performance.now() },
      ...(credits !== null ? { credits } : {}),
    });
    if (credits === null) void this.refresh();
    return vehicle;
  }

  async sell(vehicleId: string): Promise<boolean> {
    const out = await this.run(() => invokeWorldTool("vehicles.sell", { vehicleId }));
    if (!out) return false;
    const credits = creditsOf(out.output);
    this.set({
      mine: this.state.mine.filter((v) => v.id !== vehicleId),
      ...(credits !== null ? { credits } : {}),
    });
    if (credits === null) void this.refresh();
    return true;
  }

  async paint(vehicleId: string, color: string): Promise<boolean> {
    const out = await this.run(() => invokeWorldTool("vehicles.paint", { vehicleId, color }));
    if (!out) return false;
    this.set({ mine: this.state.mine.map((v) => (v.id === vehicleId ? { ...v, color } : v)) });
    return true;
  }

  /** Flies the map camera to a car. */
  locate(vehicleId: string): void {
    const v = this.vehicleOf(vehicleId);
    if (!v) return;
    this.closeGaleri();
    if (this.state.drivingId) return;
    const mode = navStore.getState().mode;
    if (mode !== "orbit") navStore.setMode("orbit");
    navStore.requestFlight([v.pose.x, 1, v.pose.z], [v.pose.x + 14, 11, v.pose.z + 18]);
  }

  /** Gets in: the server marks the car as driving, the rig takes the camera. */
  async enter(vehicleId: string): Promise<boolean> {
    const v = this.vehicleOf(vehicleId);
    if (!v || this.state.drivingId) return false;
    if (v.id === this.state.testCar?.id) {
      this.startDriving(v);
      return true;
    }
    const out = await this.run(() => invokeWorldTool("vehicles.enter", { vehicleId }));
    if (!out) return false;
    const server = parseVehicle(obj(out.output)["vehicle"]) ?? v;
    this.closeGaleri();
    const w = navStore.walker;
    this.boardFrom = { x: w.x, z: w.z, facing: locoStatus.facing };
    this.exitRequested = false;
    this.car = null; // the rig seeds it from the pose
    this.speed = 0;
    this.pendingEnter = server.id;
    this.set({
      drivingId: server.id,
      mine: this.state.mine.map((x) => (x.id === server.id ? { ...server, state: "driving" } : x)),
      near: null,
    });
    // The rig renders in a live (continuous) mode.
    const mode = navStore.getState().mode;
    if (mode !== "first_person" && mode !== "third_person") navStore.setMode("third_person");
    return true;
  }

  /** Parks the car where it stands and goes back to walking next to it. */
  /** Where the walker stood when getting in (world), for the walk to the door. */
  boardFrom: { x: number; z: number; facing: number } | null = null;
  /** The player asked to get out (the rig stops the car and plays the way out). */
  exitRequested = false;
  /** The boarding phase (HUD, tests): walk, open, in, close, start, drive, stop, outOpen, out, outClose. */
  boardPhase = "";

  /** Asks to get out: the car stops, the door opens, the character steps out, then `exit`. */
  requestExit(): void {
    if (!this.state.drivingId) return;
    this.exitRequested = true;
  }

  /** Parks the car where it stands; the walker appears at `at` (the door), or beside the car. */
  async exit(at?: { x: number; z: number }): Promise<void> {
    const id = this.state.drivingId;
    const car = this.car;
    if (!id) return;
    this.exitRequested = false;
    this.boardPhase = "";
    const pose = car
      ? { x: car.x, z: car.z, yawDeg: radToDeg(car.yaw) }
      : (this.vehicleOf(id)?.pose ?? { x: 0, z: 0, yawDeg: 0 });
    const odometerM = (this.vehicleOf(id)?.odometerM ?? 0) + (car?.distance ?? 0);
    const test = id === this.state.testCar?.id;
    this.set({
      ...(test ? { testCar: null, testEndsAt: 0 } : {}),
      drivingId: null,
      mine: this.state.mine.map((v) =>
        v.id === id ? withoutDriver({ ...v, state: "parked", pose, odometerM }) : v,
      ),
    });
    // The walker appears on the car's left, facing where the car pointed.
    if (car) {
      const side = 2.3;
      navStore.walker = {
        ...navStore.walker,
        x: at?.x ?? car.x + Math.cos(car.yaw) * side,
        z: at?.z ?? car.z - Math.sin(car.yaw) * side,
        y: 0,
        vy: 0,
        yaw: wrapAngle(car.yaw + Math.PI),
        pitch: 0,
        onGround: true,
      };
    }
    this.car = null;
    this.speed = 0;
    this.braking = false;
    if (test) {
      if (this.testTimer) clearTimeout(this.testTimer);
      this.testTimer = null;
      return;
    }
    try {
      const out = await invokeWorldTool("vehicles.exit", { vehicleId: id, pose });
      const server = parseVehicle(obj(out.output)["vehicle"]);
      if (server && this.state.drivingId === null) {
        this.set({ mine: this.state.mine.map((v) => (v.id === server.id ? server : v)) });
      }
    } catch {
      // The parked pose stays local; the next refresh reconciles it.
    }
  }

  /** The rig took the pending enter and seeded its state. */
  takePendingEnter(): VehicleEntity | undefined {
    const id = this.pendingEnter;
    this.pendingEnter = null;
    return id ? this.vehicleOf(id) : undefined;
  }

  // ------------------------------------------------------------------ realtime

  /** Sends the driven car's pose (the rig calls this at most ten times a second). */
  sendPose(): void {
    const id = this.state.drivingId;
    const car = this.car;
    if (!id || !car || id === this.state.testCar?.id) return;
    vehicleHub.sendPose({
      vehicleId: id,
      x: car.x,
      z: car.z,
      yawDeg: radToDeg(car.yaw),
      speed: this.speed,
      steer: Math.round(car.steer * 1000) / 1000,
      lights:
        (this.visual.lamps.head > 1 ? LIGHT.head : 0) |
        (this.braking ? LIGHT.brake : 0) |
        (this.visual.lamps.reverse > 0 ? LIGHT.reverse : 0) |
        (this.indicator === "left" || this.indicator === "hazard" ? LIGHT.left : 0) |
        (this.indicator === "right" || this.indicator === "hazard" ? LIGHT.right : 0),
    });
  }

  private onRemotePose(m: PoseMessage): void {
    if (m.vehicleId === this.state.drivingId) return;
    this.poses.set(m.vehicleId, {
      x: m.x,
      z: m.z,
      yaw: degToRad(m.yawDeg),
      speed: m.speed,
      ...(m.steer !== undefined ? { steer: m.steer } : {}),
      ...(m.lights !== undefined ? { lights: m.lights } : {}),
      at: performance.now(),
    });
  }
}

export const vehicleStore = new VehicleStore();

export function useVehicles<T>(
  select: (s: VehiclesState) => T,
  store: VehicleStore = vehicleStore,
): T {
  return useSyncExternalStore(store.subscribe, () => select(store.getState()));
}
